import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { CheckCircle2, Circle, Rocket, X, ArrowRight, PartyPopper, Coins } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const CREATOR_ROLES = ["creator", "educator", "admin"];

// New-creator activation checklist. Self-guards: only fetches for creator-type
// roles. On completion it celebrates once (confetti + bonus credits), then hides.
export function OnboardingCard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [reward, setReward] = useState(null);
  const claimed = useRef(false);

  useEffect(() => {
    if (!user || !CREATOR_ROLES.includes(user.role)) return;
    api.get("/growth/onboarding").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  // Celebrate + claim once when newly complete and not yet rewarded.
  useEffect(() => {
    if (data && data.complete && !data.reward_claimed && !claimed.current) {
      claimed.current = true;
      confetti({ particleCount: 180, spread: 90, origin: { y: 0.6 } });
      api.post("/growth/onboarding/claim-reward").then((r) => setReward(r.data)).catch(() => setReward({ awarded: data.reward_credits }));
    }
  }, [data]);

  if (!data || hidden) return null;
  if (data.dismissed) return null;

  // Completion celebration state
  if (data.complete) {
    if (data.reward_claimed && !reward) return null; // already claimed previously
    return (
      <div className="rounded-2xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/15 to-transparent p-5 mb-6" data-testid="onboarding-complete">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 grid place-items-center shrink-0"><PartyPopper className="w-5 h-5 text-emerald-500" /></div>
          <div className="flex-1">
            <div className="font-display font-bold text-lg">You're all set! 🎉</div>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">You've created, published, shared and sold — the full creator loop. Nice work!</p>
            {reward?.awarded > 0 && (
              <div className="inline-flex items-center gap-1.5 mt-2 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-3 py-1 text-sm font-bold" data-testid="onboarding-reward">
                <Coins className="w-4 h-4" /> +{reward.awarded} bonus credits added
              </div>
            )}
          </div>
          <button onClick={() => setHidden(true)} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="onboarding-complete-dismiss"><X className="w-4 h-4" /></button>
        </div>
      </div>
    );
  }

  const next = data.steps.find((s) => !s.done);
  const dismiss = () => { setHidden(true); api.post("/growth/onboarding/dismiss").catch(() => {}); };

  return (
    <div className="rounded-2xl border border-[hsl(var(--primary))]/30 bg-gradient-to-br from-[#1f6feb]/10 to-transparent p-5 mb-6" data-testid="onboarding-card">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/15 grid place-items-center shrink-0"><Rocket className="w-5 h-5 text-[hsl(var(--primary))]" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold flex items-center gap-2">Get set up on GameView <span className="text-xs font-normal text-[hsl(var(--muted-foreground))]">{data.done}/{data.total} done</span></div>
          <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden mt-2 max-w-xs">
            <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] transition-all" style={{ width: `${(data.done / data.total) * 100}%` }} />
          </div>
        </div>
        <button onClick={dismiss} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="onboarding-dismiss" title="Dismiss"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {data.steps.map((s) => (
          <button key={s.key} onClick={() => !s.done && navigate(s.route)} disabled={s.done}
            className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition-colors ${s.done ? "border-[hsl(var(--border))] opacity-70" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/5"}`}
            data-testid={`onboarding-step-${s.key}`}>
            {s.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" /> : <Circle className="w-5 h-5 text-[hsl(var(--muted-foreground))] shrink-0" />}
            <div className="min-w-0">
              <div className={`text-sm font-semibold ${s.done ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>{s.label}</div>
              {!s.done && <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{s.hint}</div>}
            </div>
          </button>
        ))}
      </div>
      {next && (
        <Button onClick={() => navigate(next.route)} className="rounded-full mt-4" data-testid="onboarding-next">
          {next.label} <ArrowRight className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

export default OnboardingCard;
